import React, { useMemo } from "react";
import {
  Box,
  Card,
  Grid,
  Skeleton,
  Typography,
  Chip,
  useTheme,
  useMediaQuery,
  Stack,
  Divider,
  LinearProgress,
} from "@material-ui/core";
import {
  EmojiEvents,
  People,
  TrendingUp,
  Speed,
  CheckCircle,
  AccessTime,
} from "@material-ui/icons";
import { fNumber, fPercent } from "src/utils/formatNumber";
import Chart from "react-apexcharts";
import useChart from "../charts/use-chart";

export default function ComparativaEquipoEmpleado({ data, loading }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Procesar datos para el radar chart
  const radarData = useMemo(() => {
    if (!data?.metricas) return { categories: [], series: [] };

    const { empleado, promedioEquipo } = data.metricas;

    // Normalizar métricas a escala 0-100 para mejor visualización
    const normalizeValue = (value, max = 100) => {
      return Math.min(value, max);
    };

    return {
      categories: [
        "Tareas Completadas",
        "Tiempo Resolución",
        "Cumplimiento",
        "Eficiencia",
      ],
      series: [
        {
          name: "Tú",
          data: [
            normalizeValue(empleado.tareasCompletadas, 150),
            // Invertir tiempo (menos es mejor): 100 - (tiempo * 10)
            Math.max(0, 100 - empleado.tiempoPromedioResolucion * 10),
            empleado.tasaCumplimiento,
            empleado.eficiencia,
          ],
        },
        {
          name: "Promedio del Equipo",
          data: [
            normalizeValue(promedioEquipo.tareasCompletadas, 150),
            Math.max(0, 100 - promedioEquipo.tiempoPromedioResolucion * 10),
            promedioEquipo.tasaCumplimiento,
            promedioEquipo.eficiencia,
          ],
        },
      ],
    };
  }, [data?.metricas]);

  // Configuración del radar chart
  const radarOptions = useChart({
    chart: {
      type: "radar",
      toolbar: { show: false },
    },
    colors: ["#2196f3", "#e0e0e0"],
    stroke: {
      width: 2,
    },
    fill: {
      opacity: [0.3, 0.1],
    },
    markers: {
      size: 4,
    },
    xaxis: {
      categories: radarData.categories,
      labels: {
        style: {
          fontSize: isMobile ? "9px" : "11px",
        },
      },
    },
    yaxis: {
      show: false,
      max: 100,
    },
    legend: {
      position: "bottom",
      horizontalAlign: "center",
      fontSize: isMobile ? "11px" : "13px",
    },
    tooltip: {
      y: {
        formatter: (value, { seriesIndex, dataPointIndex }) => {
          // Formateo especial para tiempo de resolución
          if (dataPointIndex === 1) {
            const tiempoReal =
              seriesIndex === 0
                ? data?.metricas?.empleado?.tiempoPromedioResolucion
                : data?.metricas?.promedioEquipo?.tiempoPromedioResolucion;
            return `${tiempoReal} días`;
          }
          // Para las demás métricas
          if (dataPointIndex === 0) {
            return `${Math.round(value)} tareas`;
          }
          return `${Math.round(value)}%`;
        },
      },
    },
  });

  // Función para obtener el badge según percentil
  const getPercentilBadge = (percentil) => {
    if (percentil >= 90)
      return {
        label: "Top 10% - Elite",
        color: "#4caf50",
        bgcolor: "#e8f5e9",
        // icon: "🏆",
      };
    if (percentil >= 80)
      return {
        label: "Top 20% - Excelente",
        color: "#2196f3",
        bgcolor: "#e3f2fd",
        // icon: "⭐",
      };
    if (percentil >= 50)
      return {
        label: "Top 50% - Sobre el promedio",
        color: "#ff9800",
        bgcolor: "#fff3e0",
        // icon: "👍",
      };
    return {
      label: "Necesita apoyo",
      color: "#f44336",
      bgcolor: "#ffebee",
      //   icon: "📈",
    };
  };

  // Función para comparar métricas
  const compareMetric = (empleado, equipo) => {
    const diff = empleado - equipo;
    const percentage = equipo > 0 ? (diff / equipo) * 100 : 0;
    return {
      diff: Math.abs(diff),
      percentage: Math.abs(percentage),
      isBetter: diff > 0,
    };
  };

  if (loading) {
    return (
      <Card sx={{ p: { xs: 2, sm: 3 }, height: "100%" }}>
        <Skeleton variant="text" width="60%" height={32} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={300} />
      </Card>
    );
  }

  const { empleado, promedioEquipo, ranking } = data?.metricas || {};
  const percentilBadge = getPercentilBadge(ranking?.percentil || 0);

  // Comparaciones
  const tareasComparison = compareMetric(
    empleado?.tareasCompletadas || 0,
    promedioEquipo?.tareasCompletadas || 0,
  );
  const eficienciaComparison = compareMetric(
    empleado?.eficiencia || 0,
    promedioEquipo?.eficiencia || 0,
  );

  return (
    <Card sx={{ p: { xs: 2, sm: 3 }, height: "100%" }}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={1}
      >
        <Typography variant={isMobile ? "subtitle1" : "h6"} fontWeight="bold">
          Comparativa con el Equipo
        </Typography>
        <Chip
          icon={<People sx={{ fontSize: isMobile ? 16 : 18 }} />}
          label={`${ranking?.totalEmpleados || 0} empleados`}
          size="small"
          variant="outlined"
          sx={{
            borderColor: "primary.main",
            color: "primary.main",
            fontWeight: "medium",
          }}
        />
      </Box>
      <Divider sx={{ mb: 2 }} />

      <Grid container spacing={2}>
        {/* Columna izquierda - Ranking y Métricas */}
        <Grid item xs={12} lg={5}>
          <Stack spacing={2}>
            {/* Badge de percentil */}
            <Box
              sx={{
                p: 2,
                bgcolor: percentilBadge.bgcolor,
                borderRadius: 2,
                border: `2px solid ${percentilBadge.color}`,
                textAlign: "center",
              }}
            >
              <EmojiEvents
                sx={{
                  fontSize: isMobile ? 40 : 48,
                  color: percentilBadge.color,
                  mb: 1,
                }}
              />
              <Typography
                variant="h3"
                sx={{ mb: 0.5 }}
                role="img"
                aria-label="trophy"
              >
                {percentilBadge.icon}
              </Typography>
              <Typography
                variant={isMobile ? "body1" : "h6"}
                sx={{ color: percentilBadge.color }}
                fontWeight="bold"
              >
                {percentilBadge.label}
              </Typography>
              <Typography
                variant="caption"
                color="textSecondary"
                display="block"
                mt={0.5}
              >
                Posición {ranking?.posicion || 0} de{" "}
                {ranking?.totalEmpleados || 0}
              </Typography>
            </Box>

            {/* Comparación de tareas */}
            <Box
              sx={{
                p: 2,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
              }}
            >
              <Box display="flex" alignItems="center" mb={1}>
                <CheckCircle
                  sx={{
                    color: "primary.main",
                    mr: 1,
                    fontSize: isMobile ? 18 : 20,
                  }}
                />
                <Typography variant="caption" fontWeight="bold">
                  Tareas Completadas
                </Typography>
              </Box>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={0.5}
              >
                <Typography variant="body2" color="textSecondary">
                  Tú
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="primary.main">
                  {fNumber(empleado?.tareasCompletadas || 0)}
                </Typography>
              </Box>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="body2" color="textSecondary">
                  Promedio equipo
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {fNumber(promedioEquipo?.tareasCompletadas || 0)}
                </Typography>
              </Box>
              {tareasComparison.isBetter && (
                <Box
                  sx={{
                    mt: 1,
                    p: 1,
                    bgcolor: "success.lighter",
                    borderRadius: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <TrendingUp
                    sx={{ color: "success.main", fontSize: 16, mr: 0.5 }}
                  />
                  <Typography
                    variant="caption"
                    color="success.main"
                    fontWeight="bold"
                  >
                    +{fPercent(tareasComparison.percentage)} que el promedio
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Comparación de eficiencia */}
            <Box
              sx={{
                p: 2,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
              }}
            >
              <Box display="flex" alignItems="center" mb={1}>
                <Speed
                  sx={{
                    color: "warning.main",
                    mr: 1,
                    fontSize: isMobile ? 18 : 20,
                  }}
                />
                <Typography variant="caption" fontWeight="bold">
                  Eficiencia General
                </Typography>
              </Box>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
              >
                <Typography variant="body2" color="textSecondary">
                  Tu eficiencia
                </Typography>
                <Chip
                  label={fPercent(empleado?.eficiencia || 0)}
                  size="small"
                  sx={{
                    bgcolor: eficienciaComparison.isBetter
                      ? "success.main"
                      : "warning.main",
                    color: "white",
                    fontWeight: "bold",
                  }}
                />
              </Box>
              <LinearProgress
                variant="determinate"
                value={empleado?.eficiencia || 0}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  bgcolor: "grey.200",
                  "& .MuiLinearProgress-bar": {
                    bgcolor: eficienciaComparison.isBetter
                      ? "success.main"
                      : "warning.main",
                    borderRadius: 4,
                  },
                }}
              />
              <Typography
                variant="caption"
                color="textSecondary"
                display="block"
                mt={0.5}
                textAlign="center"
              >
                Promedio: {fPercent(promedioEquipo?.eficiencia || 0)}
              </Typography>
            </Box>

            {/* Tiempo de resolución */}
            <Box
              sx={{
                p: 2,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
              }}
            >
              <Box display="flex" alignItems="center" mb={1}>
                <AccessTime
                  sx={{
                    color: "info.main",
                    mr: 1,
                    fontSize: isMobile ? 18 : 20,
                  }}
                />
                <Typography variant="caption" fontWeight="bold">
                  Tiempo Promedio
                </Typography>
              </Box>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="baseline"
              >
                <Typography variant="body2" color="textSecondary">
                  Tu promedio
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="info.main">
                  {fNumber(empleado?.tiempoPromedioResolucion || 0)} días
                </Typography>
              </Box>
              <Typography
                variant="caption"
                color="textSecondary"
                display="block"
                mt={0.5}
              >
                Equipo: {fNumber(promedioEquipo?.tiempoPromedioResolucion || 0)}{" "}
                días
              </Typography>
            </Box>
          </Stack>
        </Grid>

        {/* Columna derecha - Radar Chart */}
        <Grid item xs={12} lg={7}>
          <Box>
            <Typography
              variant="subtitle2"
              gutterBottom
              color="textSecondary"
              fontWeight="medium"
            >
              Análisis Multidimensional
            </Typography>
            {radarData.series[0]?.data?.length > 0 ? (
              <Box sx={{ height: { xs: 350, sm: 400 } }}>
                <Chart
                  options={radarOptions}
                  series={radarData.series}
                  type="radar"
                  height="100%"
                />
              </Box>
            ) : (
              <Box
                sx={{
                  height: 350,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "background.neutral",
                  borderRadius: 2,
                }}
              >
                <Typography variant="body2" color="textSecondary">
                  No hay datos suficientes para comparar
                </Typography>
              </Box>
            )}

            {/* Nota explicativa */}
            <Box
              sx={{
                mt: 2,
                p: 1.5,
                bgcolor: "info.lighter",
                borderRadius: 1,
              }}
            >
              <Typography variant="caption" color="info.dark">
                <strong>💡 Tip:</strong> El gráfico compara tu desempeño (azul)
                con el promedio del equipo (gris). Cuanto más se extiende hacia
                afuera, mejor el rendimiento.
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Card>
  );
}
